/**
 * CameraMapPage (shared) — cameras pinned on a floor plan, beside the live alert panel.
 *
 * The spatial counterpart to AlertHexagonPage: same colour language, same docked panel, but
 * the canvas is a real plan of the premises instead of an abstract site→group clustering, so
 * an alert reads as "loading bay, north door" rather than "CAM-17".
 *
 * A deployment holds many layouts (floors, buildings, premises) and each camera belongs to
 * exactly ONE of them — enforced by a unique constraint on the node. That partition is what
 * lets the right-hand panel be scoped to the active layout: a control room watching Floor 2
 * is not interrupted by the loading bay, and no alert is shown twice or lost.
 *
 * Node-agnostic. `api` (MapLayoutApi) owns layout storage, `alertsApi` (HexAlertsApi) owns
 * realtime, and the per-camera player is injected — local SFUPlayer, cloud HexLivePlayer.
 *
 * Not routed to directly: AlertBoardPage is what the apps mount, and it renders this or
 * AlertHexagonPage depending on the toggle it slots into `headerSlot`.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowsPointingOutIcon,
  CheckIcon,
  MapPinIcon,
  MinusIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import HexCanvas, { type HexCanvasHandle, type Viewport } from '../components/hexagon/HexCanvas';
import ClickPreviewCard from '../components/hexagon/ClickPreviewCard';
import HexAlertPopover from '../components/hexagon/HexAlertPopover';
import AlertTileStack from '../components/hexagon/AlertTileStack';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Tooltip from '../components/common/Tooltip';
import ToolbarIdentity from '../components/common/ToolbarIdentity';
import MapMarker, { MARKER_SIZE } from '../components/map/MapMarker';
import LayoutBar from '../components/map/LayoutBar';
import CameraPalette from '../components/map/CameraPalette';
import LayoutFormModal from '../components/map/LayoutFormModal';
import PlacementDetailModal, { bearingLabel } from '../components/map/PlacementDetailModal';
import {
  ALERT_LEGEND,
  viewAlertTarget,
  type HexPlayerComponent,
  type ViewAlertRequest,
  type ViewAlertTarget,
} from '../components/hexagon/types';
import { useAlertBoard } from '../hooks/useAlertBoard';
import type {
  HexAlert,
  HexAlertsApi,
  HexCamera,
  MapLayoutApi,
  MapLayoutDetail,
  MapLayoutSummary,
  MapPlacedCamera,
  MapPlacement,
} from '../api/contracts';

/** A press that moves less than this is a click, not a pan — so click-to-place still works
 *  on a canvas whose whole job is dragging. */
const CLICK_SLOP_PX = 4;

const STORAGE_KEY = 'trinetra.mapview.layout';

export interface CameraMapPageProps {
  api: MapLayoutApi;
  alertsApi: HexAlertsApi;
  PlayerComponent: HexPlayerComponent;
  /** Caller holds `map.edit` — shows the layout and placement editing affordances. */
  canEdit?: boolean;
  /** Cloud: require a selected node; show an empty state when nodeKey is falsy. */
  requireNode?: boolean;
  /** Cloud: changes when the selected node changes → reload everything. */
  nodeKey?: string;
  className?: string;
  onViewAlert?: (alert: ViewAlertRequest) => void;
  /** Slotted at the end of the toolbar's control cluster. AlertBoardPage puts the
   *  map/honeycomb toggle here so it sits in the same place on both boards. */
  headerSlot?: ReactNode;
  /** Jump to a camera's recorded footage from the live preview. Omit when the caller lacks
   *  `recordings.view` — that is what hides the button. */
  onOpenRecordings?: (cameraId: string) => void;
  /**
   * Fired when the pin editor opens or closes. AlertBoardPage listens so it can lock the
   * view toggle while pins are unsaved — flipping to the hexagon mid-edit would unmount
   * this page and silently discard the draft.
   */
  onEditingChange?: (editing: boolean) => void;
}

export default function CameraMapPage({
  api,
  alertsApi,
  PlayerComponent,
  canEdit = false,
  requireNode = false,
  nodeKey,
  className = 'p-3 sm:p-4',
  onViewAlert,
  headerSlot,
  onOpenRecordings,
  onEditingChange,
}: CameraMapPageProps) {
  const blocked = requireNode && !nodeKey;

  const [layouts, setLayouts] = useState<MapLayoutSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<MapLayoutDetail | null>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [placedElsewhere, setPlacedElsewhere] = useState<Record<string, MapPlacedCamera>>({});
  const [loadingLayouts, setLoadingLayouts] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  // Edit mode. `draft` is the working placement set; null means view mode.
  const [draft, setDraft] = useState<MapPlacement[] | null>(null);
  const [armedCameraId, setArmedCameraId] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingLayout, setEditingLayout] = useState<MapLayoutSummary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MapLayoutSummary | null>(null);
  // The pin whose install-detail dialog is open (double-click). Separate from `selectedId`,
  // which drives the live preview — a pin can be previewing and inspected at once.
  const [detailCameraId, setDetailCameraId] = useState<string | null>(null);
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailError, setDetailError] = useState('');

  const canvasRef = useRef<HexCanvasHandle>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [vp, setVp] = useState<Viewport>({ tx: 0, ty: 0, scale: 1 });
  const [size, setSize] = useState({ w: 0, h: 0 });
  const onViewportChange = useCallback((v: Viewport) => setVp(v), []);

  const editing = draft !== null;
  const placements = draft ?? detail?.placements ?? [];

  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);

  // ── Data ──────────────────────────────────────────────────────────────────

  const storageKey = `${STORAGE_KEY}${nodeKey ? `.${nodeKey}` : ''}`;

  const reloadLayouts = useCallback(async (): Promise<MapLayoutSummary[]> => {
    const list = await api.list();
    setLayouts(list);
    return list;
  }, [api]);

  useEffect(() => {
    if (blocked) {
      setLayouts([]);
      setActiveId(null);
      setDetail(null);
      setLoadingLayouts(false);
      return;
    }
    let mounted = true;
    setLoadingLayouts(true);
    setError('');
    Promise.all([api.list(), api.placedCameras().catch(() => ({}))])
      .then(([list, placed]) => {
        if (!mounted) return;
        setLayouts(list);
        setPlacedElsewhere(placed);
        // Last layout the operator looked at, then the deployment default, then whatever
        // exists — mirroring how the multi-site view opens on its default.
        const remembered = typeof localStorage !== 'undefined' ? localStorage.getItem(storageKey) : null;
        const pick =
          list.find((l) => l.id === remembered) || list.find((l) => l.is_default) || list[0] || null;
        setActiveId(pick?.id ?? null);
      })
      .catch((e) => mounted && setError((e as Error).message || 'Could not load layouts.'))
      .finally(() => mounted && setLoadingLayouts(false));
    return () => {
      mounted = false;
    };
  }, [api, nodeKey, blocked, storageKey]);

  // Load the active layout's placements + image.
  useEffect(() => {
    if (!activeId || blocked) {
      setDetail(null);
      setImageSrc(null);
      return;
    }
    let mounted = true;
    setLoadingDetail(true);
    setDraft(null);
    setSelectedId(null);
    api
      .get(activeId)
      .then(async (d) => {
        if (!mounted) return;
        setDetail(d);
        // Deferred "Mark cameras" for a layout that had to load first. Seeded from `d`,
        // not the `detail` state, which this closure captured before the fetch.
        if (pendingMarkRef.current === activeId) {
          pendingMarkRef.current = null;
          setDraft(d.placements.map((p) => ({ ...p })));
          setSelectedId(null);
          setArmedCameraId(null);
        }
        // Resolved through the api so each node can answer differently: a plain URL on
        // local, a data: URL built from tunnelled base64 on cloud.
        const src = await api.imageUrl(d).catch(() => null);
        if (mounted) setImageSrc(src);
      })
      .catch((e) => mounted && setError((e as Error).message || 'Could not load that layout.'))
      .finally(() => mounted && setLoadingDetail(false));
    return () => {
      mounted = false;
    };
  }, [api, activeId, blocked]);

  const selectLayout = useCallback(
    (id: string) => {
      if (editing) return; // don't strand unsaved pins
      setActiveId(id);
      try {
        localStorage.setItem(storageKey, id);
      } catch {
        /* private mode — the default layout is a fine fallback */
      }
    },
    [editing, storageKey],
  );

  // ── Realtime, scoped to this layout ───────────────────────────────────────

  // Referentially stable so the board's derived lists don't recompute every render.
  const layoutCameraIds = useMemo(
    () => new Set(placements.map((p) => p.camera_id)),
    // Depend on the id list, not the array identity — dragging a pin must not re-scope
    // the alert panel and remount every live tile.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [placements.map((p) => p.camera_id).sort().join(',')],
  );

  const board = useAlertBoard({ api: alertsApi, blocked, nodeKey, cameraFilter: layoutCameraIds });
  const {
    cameras,
    cards,
    liveTiles,
    primaryCameraId,
    heldCameraIds,
    warmupCameraId,
    nameById,
    liveFor,
    recordingFor,
    colorStateFor,
    counterFor,
    latestAlertFor,
  } = board;

  const cameraById = useMemo(() => {
    const m: Record<string, HexCamera> = {};
    cameras.forEach((c) => (m[c.id] = c));
    return m;
  }, [cameras]);

  // Active-alert totals per layout, so the pill bar shows an operator that another floor is
  // lit up even while they're watching this one.
  const alertCountsByLayout = useMemo(() => {
    const out: Record<string, number> = {};
    for (const [cameraId, counter] of Object.entries(board.counters)) {
      if (!counter?.total) continue;
      // The active layout's own placements are authoritative (they include unsaved edits);
      // every other camera falls back to the estate-wide placement map.
      const layoutId = layoutCameraIds.has(cameraId)
        ? activeId
        : placedElsewhere[cameraId]?.layout_id;
      if (layoutId) out[layoutId] = (out[layoutId] || 0) + counter.total;
    }
    return out;
  }, [board.counters, placedElsewhere, layoutCameraIds, activeId]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // ── Placement editing ─────────────────────────────────────────────────────

  const dragRef = useRef<{ cameraId: string; moved: boolean; startX: number; startY: number } | null>(null);
  // Rotation in flight: the pin's frozen screen centre plus which camera is spinning.
  const rotateRef = useRef<{ cameraId: string; cx: number; cy: number } | null>(null);
  const pressRef = useRef<{ x: number; y: number } | null>(null);
  // "Mark cameras" pressed in the layout dialog for a layout that isn't on screen yet:
  // remember it, switch, and drop into the editor once that layout's pins have loaded.
  const pendingMarkRef = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);

  /** Client pixel → fraction of the plan image, clamped inside it. */
  const toFraction = useCallback(
    (clientX: number, clientY: number) => {
      const el = wrapperRef.current;
      if (!el || !detail) return null;
      const r = el.getBoundingClientRect();
      const cx = (clientX - r.left - vp.tx) / vp.scale;
      const cy = (clientY - r.top - vp.ty) / vp.scale;
      const clamp = (v: number) => Math.min(1, Math.max(0, v));
      return { x: clamp(cx / detail.image_width), y: clamp(cy / detail.image_height) };
    },
    [detail, vp],
  );

  const beginEdit = useCallback(() => {
    setDraft((detail?.placements ?? []).map((p) => ({ ...p })));
    setSelectedId(null);
    setArmedCameraId(null);
    setError('');
  }, [detail]);

  const cancelEdit = useCallback(() => {
    setDraft(null);
    setArmedCameraId(null);
    setSelectedId(null);
    setError('');
  }, []);

  /**
   * HexCanvas asks this before starting a pan. Both refs are set in `pointerdown`, which
   * always fires before the `mousedown` the canvas pans on — so a press that begins a pin
   * drag or a rotation can never also pan the plan underneath it. A ref, not state: the
   * canvas needs the answer in the same tick, before React re-renders.
   */
  const canPan = useCallback(() => !dragRef.current && !rotateRef.current, []);

  const onMarkerPointerDown = useCallback(
    (cameraId: string) => (e: React.PointerEvent) => {
      if (!editing) return;
      e.stopPropagation();
      // Capture on the marker root, not e.target: the inner pin/label nodes re-render as
      // the draft updates, and capture dies with the node it was taken on.
      e.currentTarget.setPointerCapture?.(e.pointerId);
      dragRef.current = { cameraId, moved: false, startX: e.clientX, startY: e.clientY };
      setDraggingId(cameraId);
    },
    [editing],
  );

  /**
   * Start a rotation. The pin's centre is resolved ONCE here, in client coordinates: it
   * cannot move while the camera spins, and freezing it keeps the move handler free of
   * `vp`/`placements`, which would otherwise re-subscribe the listener on every frame.
   */
  const onRotatePointerDown = useCallback(
    (cameraId: string) => (e: React.PointerEvent) => {
      const el = wrapperRef.current;
      const p = placements.find((q) => q.camera_id === cameraId);
      if (!el || !p || !detail) return;
      // Not the marker's drag — this press aims the camera, it doesn't move it.
      e.stopPropagation();
      e.preventDefault();
      const r = el.getBoundingClientRect();
      rotateRef.current = {
        cameraId,
        cx: r.left + vp.tx + p.x * detail.image_width * vp.scale,
        cy: r.top + vp.ty + p.y * detail.image_height * vp.scale,
      };
      setRotatingId(cameraId);
    },
    [placements, detail, vp],
  );

  useEffect(() => {
    if (!rotatingId) return;
    const onMove = (e: PointerEvent) => {
      const r = rotateRef.current;
      if (!r) return;
      // Bearing from the pin to the cursor: 0° is up (north), increasing clockwise —
      // the same convention the cone and the stored `rotation` use.
      const deg = (Math.atan2(e.clientX - r.cx, -(e.clientY - r.cy)) * 180) / Math.PI;
      const raw = (deg + 360) % 360;
      // Shift snaps to 15° for aligning with a wall or corridor; free aim otherwise.
      const next = e.shiftKey ? Math.round(raw / 15) * 15 % 360 : Math.round(raw);
      setDraft((prev) =>
        prev ? prev.map((p) => (p.camera_id === r.cameraId ? { ...p, rotation: next } : p)) : prev,
      );
    };
    const onUp = () => {
      rotateRef.current = null;
      setRotatingId(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [rotatingId]);

  useEffect(() => {
    if (!draggingId) return;
    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d) return;
      // Ignore sub-slop jitter: a click that wobbles a pixel must still SELECT the pin,
      // because selecting is what reveals its rotation handle.
      if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) <= CLICK_SLOP_PX) return;
      const f = toFraction(e.clientX, e.clientY);
      if (!f) return;
      d.moved = true;
      setDraft((prev) =>
        prev ? prev.map((p) => (p.camera_id === d.cameraId ? { ...p, x: f.x, y: f.y } : p)) : prev,
      );
    };
    const onUp = () => {
      const d = dragRef.current;
      // A press that never moved is a selection, not a drag.
      if (d && !d.moved) setSelectedId(d.cameraId);
      dragRef.current = null;
      setDraggingId(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [draggingId, toFraction]);

  // Click-to-place. The canvas swallows presses as pans, so a press is only treated as a
  // placement click when the pointer barely moved between down and up.
  const onPlanPointerDown = useCallback((e: React.PointerEvent) => {
    pressRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const onPlanPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const start = pressRef.current;
      pressRef.current = null;
      if (!editing || !armedCameraId || !start) return;
      if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > CLICK_SLOP_PX) return;
      const f = toFraction(e.clientX, e.clientY);
      if (!f) return;
      const cameraId = armedCameraId;
      setDraft((prev) => {
        const next = (prev ?? []).filter((p) => p.camera_id !== cameraId);
        return [...next, { camera_id: cameraId, x: f.x, y: f.y, rotation: 0 }];
      });
      setSelectedId(cameraId);
      setArmedCameraId(null);
    },
    [editing, armedCameraId, toFraction],
  );

  const removePlacement = useCallback((cameraId: string) => {
    setDraft((prev) => (prev ? prev.filter((p) => p.camera_id !== cameraId) : prev));
    setSelectedId((cur) => (cur === cameraId ? null : cur));
  }, []);

  const setRotation = useCallback((cameraId: string, rotation: number) => {
    setDraft((prev) => (prev ? prev.map((p) => (p.camera_id === cameraId ? { ...p, rotation } : p)) : prev));
  }, []);

  /** Only the persisted columns — the joined camera_name/camera_status must not be sent back. */
  const toWire = (p: MapPlacement): MapPlacement => ({
    camera_id: p.camera_id,
    x: p.x,
    y: p.y,
    rotation: p.rotation,
    fov_deg: p.fov_deg,
    mount_height_m: p.mount_height_m,
    tilt_deg: p.tilt_deg,
    location_note: p.location_note,
    label: p.label,
  });

  async function savePlacements() {
    if (!activeId || !draft) return;
    setSaving(true);
    setError('');
    try {
      const saved = await api.setPlacements(activeId, draft.map(toWire));
      setDetail(saved);
      setDraft(null);
      setArmedCameraId(null);
      // The estate-wide map and the per-layout counts both moved.
      const [placed] = await Promise.all([api.placedCameras().catch(() => placedElsewhere), reloadLayouts()]);
      setPlacedElsewhere(placed);
    } catch (e) {
      // The node answers 409 naming the camera and the layout already holding it, which is
      // the whole point of the one-layout-per-camera rule being a constraint.
      setError((e as Error).message || 'Could not save the placements.');
    } finally {
      setSaving(false);
    }
  }

  /**
   * Save one pin's install detail from its dialog.
   *
   * Mid-edit this only patches the draft — the operator is still arranging pins and a write
   * now would half-commit their work. In view mode it writes straight through, so a survey
   * correction is one double-click and Save rather than a trip through the drag editor.
   */
  async function savePlacementDetail(cameraId: string, patch: Partial<MapPlacement>) {
    if (editing) {
      setDraft((prev) => (prev ? prev.map((p) => (p.camera_id === cameraId ? { ...p, ...patch } : p)) : prev));
      setDetailCameraId(null);
      return;
    }
    if (!activeId || !detail) return;
    setDetailSaving(true);
    setDetailError('');
    try {
      const next = detail.placements.map((p) => (p.camera_id === cameraId ? { ...p, ...patch } : p));
      setDetail(await api.setPlacements(activeId, next.map(toWire)));
      setDetailCameraId(null);
    } catch (e) {
      setDetailError((e as Error).message || 'Could not save the camera detail.');
    } finally {
      setDetailSaving(false);
    }
  }

  /** Remove a pin from the dialog. Patches the draft mid-edit; writes through otherwise. */
  async function removePlacementFromDialog(cameraId: string) {
    if (editing) {
      removePlacement(cameraId);
      setDetailCameraId(null);
      return;
    }
    if (!activeId || !detail) return;
    setDetailSaving(true);
    setDetailError('');
    try {
      const next = detail.placements.filter((p) => p.camera_id !== cameraId);
      setDetail(await api.setPlacements(activeId, next.map(toWire)));
      setDetailCameraId(null);
      setSelectedId((cur) => (cur === cameraId ? null : cur));
      const [placed] = await Promise.all([api.placedCameras().catch(() => placedElsewhere), reloadLayouts()]);
      setPlacedElsewhere(placed);
    } catch (e) {
      setDetailError((e as Error).message || 'Could not remove the camera.');
    } finally {
      setDetailSaving(false);
    }
  }

  // ── Layout CRUD ───────────────────────────────────────────────────────────

  async function createLayout(
    input: Parameters<MapLayoutApi['create']>[0] & { is_default?: boolean },
    opts?: { thenMark?: boolean },
  ) {
    const created = await api.create(input);
    const list = await reloadLayouts();
    const id = created.id || list[list.length - 1]?.id || null;
    // "Create & mark cameras": the editor can't open until the new layout's (empty)
    // placements and image have loaded, so hand off through the same pending flag the
    // Edit dialog uses and let the detail effect start it.
    if (opts?.thenMark && id) pendingMarkRef.current = id;
    setActiveId(id);
  }

  async function updateLayout(id: string, patch: Parameters<MapLayoutApi['update']>[1]) {
    await api.update(id, patch);
    await reloadLayouts();
    // An image swap must invalidate what's on screen; re-fetching the detail does both.
    if (id === activeId) {
      const d = await api.get(id);
      setDetail(d);
      setImageSrc(await api.imageUrl(d).catch(() => null));
    }
  }

  async function setDefaultLayout(layout: MapLayoutSummary) {
    await api.update(layout.id, { is_default: true }).catch((e) => setError((e as Error).message));
    await reloadLayouts();
  }

  async function deleteLayout() {
    const target = deleteTarget;
    if (!target) return;
    await api.delete(target.id);
    setDeleteTarget(null);
    const list = await reloadLayouts();
    setPlacedElsewhere(await api.placedCameras().catch(() => ({})));
    // Only move off the deleted layout — deleting a different one from the dropdown
    // shouldn't yank the operator away from the plan they're watching.
    if (target.id === activeId) {
      setActiveId(list.find((l) => l.is_default)?.id || list[0]?.id || null);
    }
  }

  // ── Overlays ──────────────────────────────────────────────────────────────

  const placementById = useMemo(() => {
    const m: Record<string, MapPlacement> = {};
    placements.forEach((p) => (m[p.camera_id] = p));
    return m;
  }, [placements]);

  /** Screen position of a pin, for the floating preview / popover. */
  const screenOf = useCallback(
    (p: MapPlacement) => {
      if (!detail) return { x: 0, y: 0 };
      return {
        x: vp.tx + p.x * detail.image_width * vp.scale,
        y: vp.ty + p.y * detail.image_height * vp.scale,
      };
    },
    [detail, vp],
  );

  // The pin is counter-scaled, so its on-screen half-width is constant.
  const markerHalfW = MARKER_SIZE / 2;

  const selectedPlacement = selectedId ? placementById[selectedId] : undefined;
  const hoveredPlacement = hoveredId ? placementById[hoveredId] : undefined;
  const detailPlacement = detailCameraId ? placementById[detailCameraId] : null;

  // Single click previews, double click opens the install dialog — so the preview must WAIT
  // to see whether a second click is coming. Without the delay a double-click would flash a
  // live player open and shut behind the dialog, tearing up a WebRTC subscription for
  // nothing. 250ms is the usual system double-click window.
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (clickTimerRef.current) clearTimeout(clickTimerRef.current); }, []);

  const handleMarkerClick = useCallback((cameraId: string) => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      setSelectedId((cur) => (cur === cameraId ? null : cameraId));
    }, 250);
  }, []);

  const handleMarkerDoubleClick = useCallback((cameraId: string) => {
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    setDetailError('');
    setDetailCameraId(cameraId);
  }, []);

  /** The surveyed install detail, formatted for the hover card. Always shows facing and
   *  field of view (every pin has them); height and tilt read "—" until surveyed. */
  const hoverDetails = useMemo(() => {
    if (!hoveredPlacement) return undefined;
    const p = hoveredPlacement;
    return [
      { label: 'Facing', value: `${Math.round(p.rotation ?? 0)}° ${bearingLabel(p.rotation ?? 0)}` },
      { label: 'Field of view', value: `${Math.round(p.fov_deg ?? 90)}°` },
      { label: 'Height', value: p.mount_height_m == null ? '—' : `${p.mount_height_m} m` },
      { label: 'Tilt', value: p.tilt_deg == null ? '—' : `${p.tilt_deg}°` },
    ];
  }, [hoveredPlacement]);

  /**
   * Every alert the panel is showing, in the order it shows them, reduced to what a detail view
   * needs. The click copies this, so paging with the arrow keys stays stable while the record is
   * open even as the live feed moves on.
   */
  const alertSiblings = useMemo<ViewAlertTarget[]>(
    () => cards.map((c) => viewAlertTarget(c, nameById)),
    [cards, nameById],
  );

  const handleViewAlert = useCallback(
    (alert: HexAlert) =>
      onViewAlert?.({
        ...viewAlertTarget(alert, nameById),
        siblings: alertSiblings,
        // A live tile's alert is also in `cards` (those hold every active alert), but if it
        // somehow isn't, -1 → 0 makes this a list of one rather than a broken cursor.
        index: Math.max(0, alertSiblings.findIndex((s) => !!alert.id && s.id === alert.id)),
      }),
    [onViewAlert, nameById, alertSiblings],
  );

  const nameOf = (cameraId: string) =>
    cameraById[cameraId]?.name || placementById[cameraId]?.camera_name || cameraId;

  /**
   * A camera missing from the alert roster still gets a pin — dropping it would make the
   * plan quietly wrong. It falls back to the status denormalized onto the placement row,
   * read the same way the rest of the system reads connectivity: `streaming`/`recording`
   * mean up, and `connecting`/`error` do NOT (a dead camera loops through them forever).
   */
  const camFor = (cameraId: string): HexCamera => {
    const known = cameraById[cameraId];
    if (known) return known;
    const status = placementById[cameraId]?.camera_status;
    const up = status === 'streaming' || status === 'recording';
    return {
      id: cameraId,
      name: nameOf(cameraId),
      live: up,
      recording: up,
      online: up,
      groupId: null,
      groupName: null,
      siteId: '',
      siteName: null,
    };
  };

  return (
    <div className={`flex h-full flex-col gap-3 ${className}`}>
      {/* Toolbar — the same three zones as the hexagon board this page shares a toggle with, so
          the bar cannot reflow when the operator flips the view: left = the layout on screen
          (the thing the operator most needs to read at a glance), centre = the view toggle,
          right = the controls, with the saved-layout dropdown carrying the per-row edit /
          default / delete actions.
          The page's own name is gone from here — the sidebar and the title bar already say
          which page this is, and the layout's name is what actually changes. Both side zones
          are `flex-1` so the toggle sits optically CENTRED between two unequal zones. */}
      <div className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-2.5 py-1.5 sm:px-3 sm:py-2">
        {/* ─── Left zone — which board, and which layout of it ───
            The headline is the LAYOUT's name, because that is what changes here and what an
            operator with three floor plans is choosing between — prefixed with what the name IS,
            since the name on its own said nothing. "Alert board" leads the second line so the
            page is still named, and the pin tile says it a second way. The rest of
            that line carries the interaction hint, which changes with the mode — mid-edit
            ("drag its knob to aim") it is the most useful text on the page. */}
        <div className="flex min-w-0 flex-1 items-center">
          <ToolbarIdentity
            icon={MapPinIcon}
            // "Floor plan:" in front of the name is the whole point — a bare "Ground Floor" in
            // a toolbar corner is a riddle unless you already know the page keeps several plans.
            kind="Floor plan:"
            title={blocked || !detail ? 'none selected' : detail.name}
            titleText={detail ? `Floor plan: ${detail.name}` : undefined}
            detail={
              blocked ? 'Alert board · no site selected' : !detail ? 'Alert board · no floor plan yet' : editing ? (
                <>
                  <span className="font-semibold uppercase tracking-wide text-brand">Editing</span>
                  {' · click the plan to place · drag a pin to move · drag its knob to aim (Shift snaps 15°)'}
                </>
              ) : (
                `Alert board · ${placements.length} camera${placements.length === 1 ? '' : 's'} on this plan · click a pin to preview, double-click for its detail`
              )
            }
          />
        </div>

        {/* ─── Centre zone — which view you're on ─── */}
        <div className="flex flex-none items-center justify-center">{headerSlot}</div>

        {/* ─── Right zone — the layout's actions, symbols only so the name keeps its room ─── */}
        <div className="flex min-w-0 flex-1 items-center justify-end gap-1.5 sm:gap-2">
          {editing ? (
            <>
              <Tooltip label={saving ? 'Saving…' : 'Save layout'} side="bottom">
                <button
                  type="button"
                  onClick={savePlacements}
                  disabled={saving}
                  aria-label="Save layout"
                  className="rounded-lg bg-brand p-1.5 text-white shadow-sm ring-1 ring-inset ring-white/10 transition-all hover:bg-brand/90 active:scale-[0.95] disabled:pointer-events-none disabled:opacity-50"
                >
                  <CheckIcon className="h-4 w-4" strokeWidth={2.5} />
                </button>
              </Tooltip>
              <Tooltip label="Discard changes" side="bottom">
                <button
                  type="button"
                  onClick={cancelEdit}
                  aria-label="Cancel"
                  className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </Tooltip>
            </>
          ) : null}

          {/* Marking is reached from a layout's own dialog (New / Edit), not from here —
              placing pins belongs to a layout, and the toolbar shouldn't imply it applies
              to whatever happens to be on screen. */}
          {!blocked && (
            <LayoutBar
              layouts={layouts}
              activeId={activeId}
              onSelect={selectLayout}
              alertCounts={alertCountsByLayout}
              disabled={editing}
              canEdit={canEdit}
              onCreate={() => {
                setEditingLayout(null);
                setFormOpen(true);
              }}
              onEdit={(l) => {
                setEditingLayout(l);
                setFormOpen(true);
              }}
              onSetDefault={setDefaultLayout}
              onDelete={setDeleteTarget}
            />
          )}

        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          {error}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-3 lg:flex-row lg:gap-4">
        {/* Edit rail — cameras to place, plus the selected pin's facing. */}
        {editing && (
          <aside className="flex max-h-[40vh] min-h-0 flex-none flex-col gap-3 rounded-lg border border-border bg-card p-3 lg:max-h-none lg:w-[240px]">
            <CameraPalette
              cameras={cameras}
              placedHere={layoutCameraIds}
              placedElsewhere={placedElsewhere}
              armedId={armedCameraId}
              onArm={setArmedCameraId}
            />
            {selectedPlacement && (
              <div className="flex-none space-y-2 border-t border-border pt-2">
                <p className="truncate text-xs font-semibold text-foreground">{nameOf(selectedPlacement.camera_id)}</p>
                <label className="block text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  Facing {Math.round(selectedPlacement.rotation ?? 0)}° {bearingLabel(selectedPlacement.rotation ?? 0)}
                </label>
                <input
                  type="range"
                  min={0}
                  max={360}
                  value={selectedPlacement.rotation || 0}
                  onChange={(e) => setRotation(selectedPlacement.camera_id, Number(e.target.value))}
                  className="w-full accent-[color:var(--brand,#015ab5)]"
                />
                {/* The full survey (height, tilt, field of view, note) lives in the pin's
                    dialog — same one a double-click opens — so the rail stays a quick
                    positioning tool rather than a second form. */}
                <button
                  type="button"
                  onClick={() => handleMarkerDoubleClick(selectedPlacement.camera_id)}
                  className="w-full rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  Placement detail…
                </button>
                <button
                  type="button"
                  onClick={() => removePlacement(selectedPlacement.camera_id)}
                  className="w-full rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                >
                  Remove from layout
                </button>
              </div>
            )}
          </aside>
        )}

        <div
          ref={wrapperRef}
          // With the alert panel hidden, the plan takes the whole height on narrow screens
          // too — the 45vh box only makes sense when something sits beside it.
          className={`relative overflow-hidden rounded-lg border border-border bg-card lg:h-auto lg:min-h-0 lg:flex-1 ${
            editing ? 'min-h-[320px] flex-1' : 'h-[45vh] min-h-[260px] flex-none'
          }`}
        >
          {blocked ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              Select a site to view its layouts.
            </div>
          ) : loadingLayouts ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading layouts…</div>
          ) : layouts.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <p className="text-sm text-muted-foreground">
                No floor plans yet. Upload one and mark your cameras on it.
              </p>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingLayout(null);
                    setFormOpen(true);
                  }}
                  className="rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-dark"
                >
                  Create layout
                </button>
              )}
            </div>
          ) : loadingDetail || !detail ? (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">Loading plan…</div>
          ) : (
            <HexCanvas
              ref={canvasRef}
              contentWidth={detail.image_width}
              contentHeight={detail.image_height}
              onViewportChange={onViewportChange}
              canPan={canPan}
              // The plan is a fixed backdrop: it stays put under the cursor and only
              // becomes draggable once zoomed past fit, where panning is the only way to
              // reach the rest of it.
              panWhenZoomedOnly
            >
              <div
                className="relative h-full w-full"
                onPointerDown={onPlanPointerDown}
                onPointerUp={onPlanPointerUp}
                style={{ cursor: editing && armedCameraId ? 'crosshair' : undefined }}
              >
                {imageSrc && (
                  // Rendered as <img>, never inline: an uploaded SVG's scripts cannot
                  // execute in an image context (the node also serves it sandboxed).
                  <img
                    src={imageSrc}
                    alt={detail.name}
                    draggable={false}
                    className="pointer-events-none absolute inset-0 h-full w-full select-none object-fill"
                  />
                )}

                {placements.map((p) => {
                  const cam = camFor(p.camera_id);
                  return (
                    <MapMarker
                      key={p.camera_id}
                      name={cam.name}
                      colorState={colorStateFor(cam)}
                      counter={counterFor(cam)}
                      live={liveFor(cam)}
                      recording={recordingFor(cam)}
                      x={p.x}
                      y={p.y}
                      imageWidth={detail.image_width}
                      imageHeight={detail.image_height}
                      scale={vp.scale}
                      rotation={p.rotation}
                      fov={p.fov_deg}
                      selected={selectedId === p.camera_id || detailCameraId === p.camera_id}
                      editable={editing}
                      dragging={draggingId === p.camera_id}
                      showRotateHandle={editing && selectedId === p.camera_id}
                      rotating={rotatingId === p.camera_id}
                      onPointerDown={onMarkerPointerDown(p.camera_id)}
                      onRotatePointerDown={onRotatePointerDown(p.camera_id)}
                      onClick={() => {
                        if (editing) return; // pointerup already handled select-vs-drag
                        handleMarkerClick(p.camera_id);
                      }}
                      onDoubleClick={() => handleMarkerDoubleClick(p.camera_id)}
                      onRemove={() => removePlacement(p.camera_id)}
                      onHoverStart={() => setHoveredId(p.camera_id)}
                      onHoverEnd={() => setHoveredId(null)}
                    />
                  );
                })}
              </div>
            </HexCanvas>
          )}

          {/* Screen-space overlay layer (not scaled by the canvas transform) */}
          {!editing && detail && (
            <div className="pointer-events-none absolute inset-0 z-30">
              {selectedPlacement &&
                (() => {
                  const cam = camFor(selectedPlacement.camera_id);
                  const p = screenOf(selectedPlacement);
                  const latest = latestAlertFor(cam.id);
                  return (
                    <ClickPreviewCard
                      cameraId={cam.id}
                      name={cam.name}
                      caption={latest?.alertName || latest?.eventType || 'live'}
                      live={liveFor(cam)}
                      recording={recordingFor(cam)}
                      counter={counterFor(cam)}
                      anchorX={p.x}
                      anchorY={p.y}
                      hexHalfW={markerHalfW}
                      viewportW={size.w}
                      viewportH={size.h}
                      PlayerComponent={PlayerComponent}
                      onOpenRecordings={onOpenRecordings}
                      onClose={() => setSelectedId(null)}
                    />
                  );
                })()}
              {hoveredPlacement &&
                hoveredId !== selectedId &&
                (() => {
                  const cam = camFor(hoveredPlacement.camera_id);
                  const p = screenOf(hoveredPlacement);
                  return (
                    <HexAlertPopover
                      alert={latestAlertFor(cam.id)}
                      name={cam.name}
                      live={liveFor(cam)}
                      recording={recordingFor(cam)}
                      counter={counterFor(cam)}
                      anchorX={p.x}
                      anchorY={p.y}
                      hexHalfW={markerHalfW}
                      viewportW={size.w}
                      viewportH={size.h}
                      details={hoverDetails}
                      note={hoveredPlacement.location_note}
                    />
                  );
                })()}
            </div>
          )}

          {/* Zoom controls. With the plan held still at fit scale, these are the only way
              to magnify it — a dead scroll wheel and no buttons would leave a large plan
              unreadable. */}
          {detail && (
            <div className="absolute right-3 top-3 z-40 flex items-center gap-1 rounded-md border border-border bg-card/90 p-0.5 shadow-card backdrop-blur-sm">
              <button
                type="button"
                title="Zoom out"
                onClick={() => canvasRef.current?.zoomBy(1 / 1.25)}
                className="flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-muted"
              >
                <MinusIcon className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                title="Zoom in"
                onClick={() => canvasRef.current?.zoomBy(1.25)}
                className="flex h-6 w-6 items-center justify-center rounded text-foreground hover:bg-muted"
              >
                <PlusIcon className="h-3.5 w-3.5" />
              </button>
              <span className="h-4 w-px bg-border" aria-hidden />
              <button
                type="button"
                title="Fit the plan to the view"
                onClick={() => canvasRef.current?.fitToView()}
                className="flex h-6 items-center gap-1 rounded px-1.5 text-xs text-foreground hover:bg-muted"
              >
                <ArrowsPointingOutIcon className="h-3.5 w-3.5" /> Fit
              </button>
            </div>
          )}

          {detail && (
            <div className="absolute bottom-3 left-3 z-40 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg border border-border bg-card/90 px-3 py-1.5 shadow-card backdrop-blur-sm">
              <span className="text-[10px] font-semibold text-foreground">
                Each pin — left dot: Live, right dot: Recording
              </span>
              <span className="h-3 w-px bg-border" aria-hidden />
              {ALERT_LEGEND.map((l) => (
                <span key={l.label} className="flex items-center gap-1.5 text-[10px] font-medium text-muted-foreground">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${l.c} ${l.pulse ? 'animate-pulse' : ''}`} />
                  {l.label}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Hidden transport warmer — see the note in useAlertBoard. Scoped to this layout's
            cameras, so switching plans warms the transport for the cameras now on screen. */}
        {warmupCameraId && (
          <div aria-hidden className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0">
            <PlayerComponent cameraId={warmupCameraId} label="warmup" className="h-px w-px" />
          </div>
        )}

        {/* The alert panel stands down while marking: placing pins is a survey task that
            wants every pixel of plan, and a wall of live tiles beside it is noise the
            operator can't act on mid-edit. It comes back on save or cancel. */}
        {!editing && (
        <aside className="flex min-h-0 flex-1 flex-col rounded-lg border border-border bg-card p-3 lg:w-[360px] lg:flex-none lg:p-4">
          <AlertTileStack
            liveTiles={liveTiles}
            persistentCameraId={primaryCameraId}
            heldCameraIds={heldCameraIds}
            cards={cards}
            nameById={nameById}
            counters={board.counters}
            PlayerComponent={PlayerComponent}
            onSelect={(id) => setSelectedId(id)}
            onExpire={board.handleTileExpire}
            onContinue={board.handleTileContinue}
            onAcknowledge={alertsApi.acknowledgeAlert ? board.handleAcknowledge : undefined}
            onResolve={alertsApi.resolveAlert ? board.handleResolve : undefined}
            onViewAlert={onViewAlert ? handleViewAlert : undefined}
            onOpenRecordings={onOpenRecordings}
            loadPreview={alertsApi.loadPreview}
            hasMore={board.hasMore}
            loadingMore={board.loadingCards}
            onLoadMore={board.loadMoreCards}
          />
        </aside>
        )}
      </div>

      <LayoutFormModal
        open={formOpen}
        layout={editingLayout}
        canEdit={canEdit}
        // Lets the dialog show the plan being edited, so "replace" is a comparison rather
        // than a guess. Cached per layout + updated_at inside the adapter.
        resolveImage={(l) => api.imageUrl(l)}
        onClose={() => setFormOpen(false)}
        onCreate={createLayout}
        onUpdate={updateLayout}
        // Straight from the layout dialog into placing pins — renaming a floor and then
        // marking its cameras is one errand, not two.
        onMarkCameras={(layout) => {
          setFormOpen(false);
          if (layout.id === activeId) {
            beginEdit();
            return;
          }
          pendingMarkRef.current = layout.id;
          selectLayout(layout.id);
        }}
      />

      {/* Double-click a pin → its surveyed install detail. Editable in place, so a
          correction after a site visit doesn't require entering the drag editor. */}
      <PlacementDetailModal
        open={!!detailPlacement}
        placement={detailPlacement}
        cameraName={detailCameraId ? nameOf(detailCameraId) : ''}
        canEdit={canEdit}
        saving={detailSaving}
        error={detailError}
        onClose={() => setDetailCameraId(null)}
        onSave={(patch) => detailCameraId && savePlacementDetail(detailCameraId, patch)}
        onRemove={() => detailCameraId && removePlacementFromDialog(detailCameraId)}
        onReposition={() => {
          const cam = detailCameraId;
          setDetailCameraId(null);
          if (!editing) beginEdit();
          setSelectedId(cam);
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete layout"
        message={`Delete "${deleteTarget?.name}"? Its camera placements are removed — the cameras themselves are untouched and become available for another layout.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={deleteLayout}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
